import React, { useState } from 'react';
import { Box, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import SourceChip from '../../../capability-setup/SourceChip';
import { sourceOf } from '../../../capability-setup/capabilitySource';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DosenTooltip from '../../../../../../components/ui/DosenTooltip';
import InfoTip from '../../../../../../components/ui/InfoTip';
import DosenButton from '../../../../../../components/ui/Buttons/DosenButton';
import { formatDate as formatHouseDate } from '../../../../../../utils/formatDate';
import { fadeInSx } from '../../../../../../components/ui/DashboardSkeletons';
import { BODY, CollapsibleRow, GapSection } from './GapsRisksPanel';
import { MathRow, TIP_HEADING, TIP_TEXT } from './TeamCapabilityScore';
import {
  capabilityColor,
  QUIZ_DROP_AT_PERCENT,
  RECENT_COUNT,
  type CapabilityLevel,
  type LevelWorking,
  type Ratio,
  type RoleplayResult
} from './teamCapability';
import { toast } from 'react-toastify';
import { apiMessage, capabilityApi } from '../../../../../../api/capability';
import { useCapabilityOrg } from '../../../../../../queries/useCapability';
import type { LevelCriteria } from '../../../capability-setup/capabilitySetup';
import { AnalysisDialog } from '../../../capability-setup/learner/RoleplayDialogs';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';

const CORRECT = '#00972D';
const INCORRECT = '#FF6A55';
const DIM = '#565656';

export const LEVEL_NAME: Record<CapabilityLevel, string> = {
  weak: 'Weak',
  developing: 'Developing',
  capable: 'Capable',
  advanced: 'Advanced'
};
export const LEVEL_COLOR: Record<CapabilityLevel, string> = {
  weak: '#FF6A55',
  developing: '#F79420',
  capable: '#22C55E',
  advanced: '#22C55E'
};

// A yyyy-mm-dd day in the house format, "Jun 30, 2026".
const formatDate = (iso: string) => formatHouseDate(new Date(`${iso}T00:00:00`));

// An empty table cell: blank, but it keeps the line's height.
const EMPTY_CELL = '\u00A0';

/** A level as it reads on a row: its name in its colour, or Not assessed. */
export const LevelLabel: React.FC<{ level: CapabilityLevel | null }> = ({ level }) => (
  <Typography
    sx={{
      flexShrink: 0,
      fontFamily: 'Inter, sans-serif',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      color: level ? LEVEL_COLOR[level] : '#868686'
    }}
  >
    {level ? LEVEL_NAME[level] : 'Not assessed'}
  </Typography>
);

/**
 * Beside a level that comes from Roleplays graded on the capability's earlier
 * levels (they were edited since): the level stands, flagged, and its tip says
 * how it gets measured against the current levels (`reassess`).
 */
export const PreviousLevelsMark: React.FC<{ reassess: string }> = ({ reassess }) => (
  <DosenTooltip
    placement="bottom"
    title={`Assessed before these levels were changed. ${reassess}`}
    componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}
  >
    <Typography
      component="span"
      sx={{
        flexShrink: 0,
        px: '4px',
        border: '1px dashed #565656',
        borderRadius: '4px',
        fontFamily: 'Inter, sans-serif',
        fontSize: 9.5,
        fontWeight: 600,
        lineHeight: '15px',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        color: '#B6B6B6',
        cursor: 'default'
      }}
    >
      Scored on earlier levels
    </Typography>
  </DosenTooltip>
);

// The three columns of a history table: result, date completed, the lesson it was taken in.
const TABLE_COLUMNS = '104px 108px minmax(0, 1fr)';
const TABLE_RULE = '1px solid #2A2A2A';
const cellText = { fontFamily: 'Inter, sans-serif', fontSize: 11.5, lineHeight: '18px', color: '#B6B6B6' };
// Each cell carries its own padding and left rule, so the column lines run
// unbroken down the table and the text sits clear of them.
const tableCell = (column: number) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  py: '5px',
  pl: column === 0 ? '2px' : '14px',
  pr: '14px',
  ...(column > 0 ? { borderLeft: TABLE_RULE } : {})
});

/** One of the five lines of a history table. An empty slot is left blank. */
const HistoryLine: React.FC<{
  result?: React.ReactNode;
  date?: string;
  source?: string;
  /** No hover of its own on the lesson name (the table has one over all its entries). */
  plain?: boolean;
  /**
   * A fourth column (Analysis). `undefined` = the table has no such column;
   * `null` = it has one, and this line has nothing in it.
   */
  action?: React.ReactNode | null;
}> = ({ result, date, source, plain = false, action }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: action === undefined ? TABLE_COLUMNS : `${TABLE_COLUMNS} 96px`,
      '&:not(:last-of-type)': { borderBottom: '1px solid #1F1F1F' },
      // Phone: result, date (and Analysis) on one line, the lesson in full on its own line beneath.
      [MOBILE_SX]: { gridTemplateColumns: action === undefined ? '96px minmax(0, 1fr)' : '96px minmax(0, 1fr) auto' }
    }}
  >
    <Box sx={tableCell(0)}>{result ?? <Typography sx={{ ...cellText, color: DIM }}>{EMPTY_CELL}</Typography>}</Box>
    <Box sx={tableCell(1)}>
      <Typography sx={{ ...cellText, whiteSpace: 'nowrap', color: date ? '#B6B6B6' : DIM }}>
        {date ? formatDate(date) : EMPTY_CELL}
      </Typography>
    </Box>
    <Box
      sx={{ ...tableCell(2), [MOBILE_SX]: { gridColumn: '1 / -1', gridRow: 2, borderLeft: 'none', pl: '2px', pt: 0 } }}
    >
      <Tooltip title={plain ? '' : source ?? ''} placement="bottom-start" enterDelay={400}>
        <Typography
          sx={{
            ...cellText,
            cursor: 'default',
            color: source ? '#FDFDFD' : DIM,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            // Phone: the source in full, wrapping in its cell.
            [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible', overflowWrap: 'break-word' }
          }}
        >
          {source ?? EMPTY_CELL}
        </Typography>
      </Tooltip>
    </Box>
    {action !== undefined && (
      <Box sx={tableCell(3)}>{action ?? <Typography sx={{ ...cellText, color: DIM }}>{EMPTY_CELL}</Typography>}</Box>
    )}
  </Box>
);

/**
 * The five lines of a history table. With a hint (the admin's view), hovering
 * anywhere over them says where to find the detail behind the results.
 */
const HistoryEntries: React.FC<{ hint?: string; children: React.ReactNode }> = ({ hint, children }) =>
  hint ? (
    <DosenTooltip title={hint} placement="bottom-start" enterDelay={300}>
      <Box sx={{ cursor: 'default' }}>{children}</Box>
    </DosenTooltip>
  ) : (
    <Box>{children}</Box>
  );

/**
 * A history table's title: a ruled band above the entries, in white capitals,
 * with the rule behind the table on an info icon.
 */
const HistoryTitle: React.FC<{ title: string; tip: React.ReactNode }> = ({ title, tip }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      px: '2px',
      py: '7px',
      borderTop: TABLE_RULE,
      borderBottom: TABLE_RULE
    }}
  >
    <Typography
      sx={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 10.5,
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#FDFDFD'
      }}
    >
      {title}
    </Typography>
    <InfoTip placement="bottom-start" title={tip} sx={{ display: 'block' }} />
  </Box>
);

const resultText = (text: string, color: string) => (
  <Typography sx={{ ...cellText, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color }}>
    {text}
  </Typography>
);

const levelSpan = (level: CapabilityLevel) => (
  <Box component="span" sx={{ color: LEVEL_COLOR[level], fontWeight: 700 }}>
    {LEVEL_NAME[level]}
  </Box>
);

/** A history tooltip: the rule under "Logic", then this person's own figures under "Breakdown". */
const HistoryTip: React.FC<{ logic: string[]; children?: React.ReactNode }> = ({ logic, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 260 }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Typography sx={TIP_HEADING}>Logic</Typography>
      {/* One paragraph per sentence. */}
      {logic.map((sentence) => (
        <Typography key={sentence} sx={TIP_TEXT}>
          {sentence}
        </Typography>
      ))}
    </Box>
    {children && (
      <Box>
        <Typography sx={TIP_HEADING}>Breakdown</Typography>
        {children}
      </Box>
    )}
  </Box>
);

/**
 * What sets one person's level on one capability: their recent Roleplays (the
 * anchor) and recent Quiz Questions (which can only lower it), the rule for
 * each on its info icon. Five lines each, the five that count; fewer done
 * leaves the rest as dashes. `who` is "Sarah Byrne has" for an admin, "You have" for the learner.
 * An admin also gets a hint over each table's entries, pointing to the Members Hub.
 */
export const HistoryTables: React.FC<{
  working: LevelWorking;
  who: string;
  whose: string;
  /** What hovering over each table's entries says (the admin's view only). */
  hints?: { roleplays: string; quizzes: string };
  /** The learner's view: adds an Analysis column to Recent Roleplays, opening Elliott's assessment of one. */
  onAnalysis?: (roleplay: RoleplayResult) => void;
}> = ({ working, who, whose, hints, onAnalysis }) => {
  const { roleplays, quizzes, roleplayMean, anchor, quizPercent, dropped, level } = working;
  const passed = quizzes.filter((q) => q.passed).length;
  // One line for each of the five that count toward the level.
  const slots = Array.from({ length: RECENT_COUNT }, (_, i) => i);

  const roleplayTip = (
    <HistoryTip
      logic={[
        `The ${RECENT_COUNT} most recent Roleplays are averaged (Weak 1, Developing 2, Capable 3, Advanced 4) and rounded down.`,
        `This anchors ${whose} level.`
      ]}
    >
      {roleplayMean !== null && anchor && (
        <>
          <MathRow label="Roleplays Counted" value={roleplays.length} />
          <MathRow label="Average" value={roleplayMean.toFixed(1)} />
          <MathRow total label="Anchor Level" value={levelSpan(anchor)} />
        </>
      )}
    </HistoryTip>
  );
  const quizTip = (
    <HistoryTip
      logic={[
        `Passing ${QUIZ_DROP_AT_PERCENT}% or fewer of the ${RECENT_COUNT} most recent Quiz Questions drops ${whose} level by one from the Roleplay anchor.`,
        'A higher pass rate never raises it.'
      ]}
    >
      {quizPercent !== null && (
        <>
          <MathRow label="Quiz Questions Counted" value={quizzes.length} />
          <MathRow label="Passed" value={`${passed} (${quizPercent}%)`} />
          {anchor && level && (
            <MathRow
              total
              label="Level"
              value={
                dropped ? (
                  <>
                    {levelSpan(anchor)} → {levelSpan(level)}
                  </>
                ) : (
                  <>{levelSpan(level)} (No Change)</>
                )
              }
            />
          )}
        </>
      )}
    </HistoryTip>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {roleplays.length === 0 && (
        <Typography sx={{ ...BODY }}>
          {who === 'You have'
            ? 'Your level will appear here as soon as you complete your first Roleplay on this capability.'
            : `A level will appear here as soon as ${who.replace(
                / has$/,
                ''
              )} completes a Roleplay on this capability.`}
        </Typography>
      )}

      <Box>
        <HistoryTitle title="Recent Roleplays" tip={roleplayTip} />
        <HistoryEntries hint={hints?.roleplays}>
          {slots.map((i) => {
            const r = roleplays[i];
            return r ? (
              <HistoryLine
                key={r.id}
                result={resultText(LEVEL_NAME[r.level], LEVEL_COLOR[r.level])}
                date={r.completedOn}
                source={r.source}
                plain={Boolean(hints)}
                action={
                  onAnalysis ? (
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Elliott's analysis of this Roleplay`}
                      onClick={() => onAnalysis(r)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        p: 0,
                        border: 'none',
                        bgcolor: 'transparent',
                        color: '#F79420',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 11.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': { filter: 'brightness(1.2)' }
                      }}
                    >
                      Analysis
                    </Box>
                  ) : undefined
                }
              />
            ) : (
              <HistoryLine key={`empty-${i}`} action={onAnalysis ? null : undefined} />
            );
          })}
        </HistoryEntries>
      </Box>

      <Box>
        <HistoryTitle title="Recent Quiz Questions" tip={quizTip} />
        <HistoryEntries hint={hints?.quizzes}>
          {slots.map((i) => {
            const q = quizzes[i];
            return q ? (
              <HistoryLine
                key={q.id}
                result={resultText(q.passed ? 'Pass' : 'Fail', q.passed ? CORRECT : INCORRECT)}
                date={q.completedOn}
                source={q.source}
                plain={Boolean(hints)}
              />
            ) : (
              <HistoryLine key={`empty-${i}`} />
            );
          })}
        </HistoryEntries>
      </Box>
    </Box>
  );
};

export interface CapabilityEmployee {
  id: string;
  name: string;
  /** Their level on this capability and the Roleplays and Quiz Questions behind it. */
  working: LevelWorking;
}

/** One capability for a team and role, as GET /v3/admin/capability/levels answers it. */
export interface CapabilityGapRow {
  id: string;
  teamId: string;
  teamName: string;
  roleName: string;
  /** The capability's id (unique across roles). */
  capabilityId?: string;
  capabilityName: string;
  /** Where it comes from: a role's own (absent), or a shared Development or Standards one. */
  source?: string;
  ratio: Ratio;
  /** Everyone in this team and role, lowest level first, not assessed last. */
  employees: CapabilityEmployee[];
  /** The capability's levels as set in Capability Setup, for a Roleplay that didn't record its own. */
  criteria?: LevelCriteria;
}

const MEMBERS_HUB_HINTS = {
  roleplays:
    "You can see Elliott's detailed analysis of why your employees failed these Roleplays from your Members Hub.",
  quizzes: 'You can see which responses your employees chose from your Members Hub.'
};

/** One employee under a capability: their name and level, opening onto what set it. */
/** The width Encourage Practice takes on an employee's row, kept for everyone so the levels line up. */
const NUDGE_COL = 128;

/**
 * Encourage Practice: nudges someone at Weak or Developing to practise the
 * capability. They get it in their bell and, when their Microsoft Teams is
 * connected, there too. Once sent it reads Nudge Sent for the rest of the visit.
 */
const EncouragePractice: React.FC<{ employee: CapabilityEmployee; capabilityId: string }> = ({
  employee,
  capabilityId
}) => {
  const org = useCapabilityOrg();
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const first = employee.name.trim().split(/\s+/)[0] || employee.name;
  const send = async () => {
    setState('sending');
    try {
      const sent = await capabilityApi.encouragePractice(employee.id, capabilityId, org);
      setState('sent');
      toast.success(
        sent.teams ? `Nudge sent to ${first} in Dosen and Microsoft Teams.` : `Nudge sent to ${first} in Dosen.`
      );
    } catch (err) {
      setState('idle');
      toast.error(apiMessage(err, `Couldn't send the nudge to ${first}. Please try again.`));
    }
  };
  // Once sent, the button stays in place, disabled, as Nudge Sent.
  if (state === 'sent') {
    return (
      <DosenButton tone="basic" sizeVariant="xs" disabled sx={{ whiteSpace: 'nowrap' }}>
        Nudge Sent
      </DosenButton>
    );
  }
  return (
    <DosenTooltip
      placement="top"
      title={`Sends ${first} a nudge, in Dosen and Microsoft Teams, to Practice Roleplay for this capability.`}
    >
      {/* span keeps the tooltip working while the button is disabled */}
      <span>
        <DosenButton
          tone="basic"
          sizeVariant="xs"
          loading={state === 'sending'}
          loadingText="Sending..."
          onClick={send}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Encourage Practice
        </DosenButton>
      </span>
    </DosenTooltip>
  );
};

const EmployeeRow: React.FC<{
  employee: CapabilityEmployee;
  capabilityName: string;
  /** The capability's id, for Encourage Practice. Without it the row has no such action. */
  capabilityId?: string;
  criteria: LevelCriteria;
}> = ({ employee, capabilityName, capabilityId, criteria }) => {
  const { level } = employee.working;
  const canNudge = Boolean(capabilityId) && (level === 'weak' || level === 'developing');
  // Elliott's analysis of one of their Roleplays, from the Analysis column.
  const [analysing, setAnalysing] = useState<RoleplayResult | null>(null);
  return (
    <CollapsibleRow
      label={employee.name}
      right={
        <>
          {/* A level from the capability's earlier levels: still counted, flagged, with how to reassess.
              Before the level, so the level and Encourage Practice columns still line up. */}
          {employee.working.onPreviousLevels && (
            <PreviousLevelsMark
              reassess={`${employee.name}'s next counted Roleplay, in an Assessment or a Graded Roleplay of their own, measures them on the current ones. To reassess the role, Run Assessment in Capabilities.`}
            />
          )}
          <Box sx={{ width: 96, display: 'flex', justifyContent: 'center' }}>
            <LevelLabel level={employee.working.level} />
          </Box>
          {/* Encourage Practice, for anyone at Weak or Developing. The click is its own: it never opens the row. */}
          {capabilityId && (
            <Box
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ width: NUDGE_COL, flexShrink: 0, display: 'flex', justifyContent: 'center' }}
            >
              {canNudge && <EncouragePractice employee={employee} capabilityId={capabilityId} />}
            </Box>
          )}
        </>
      }
    >
      <HistoryTables
        working={employee.working}
        who={`${employee.name} has`}
        whose="their"
        hints={MEMBERS_HUB_HINTS}
        onAnalysis={setAnalysing}
      />
      <AnalysisDialog
        capabilityName={capabilityName}
        roleplay={analysing}
        criteria={criteria}
        onClose={() => setAnalysing(null)}
      />
    </CollapsibleRow>
  );
};

/** A line of the Breakdown: what is counted on the left, the figure on the right. */
const BreakdownLine: React.FC<{ label: string; value: React.ReactNode; total?: boolean }> = ({
  label,
  value,
  total = false
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: '12px',
      py: '4px',
      ...(total ? { borderTop: '1px solid #2A2A2A', mt: '2px', pt: '6px' } : {})
    }}
  >
    <Typography sx={{ ...BODY, color: total ? '#FDFDFD' : '#B6B6B6', fontWeight: total ? 600 : 400 }}>
      {label}
    </Typography>
    <Typography sx={{ ...BODY, color: '#FDFDFD', fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</Typography>
  </Box>
);

const EMPLOYEES_TIP = (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <Typography sx={TIP_TEXT}>
      This breakdown shows your employees&apos; scoring across recent Roleplays and Quiz Questions. You can also see
      Elliott&apos;s detailed analysis of why your employees failed their Roleplays and Quiz Questions from your Members
      Hub. This empowers you to informatively manage and coach each employee, allowing them to perform better in their
      next attempts, therefore increasing your team&apos;s capability.
    </Typography>
    <Typography sx={TIP_TEXT}>
      Wherever you notice persistently weak capabilities, consider the following actions:
    </Typography>
    <Box component="ol" sx={{ m: 0, pl: '18px' }}>
      {[
        'Spinning up a targeted course for your team with Elliott',
        'Updating the Roleplay and/or Quiz Question to make it clearer',
        'Adding relevant documentation to your Knowledge Hub'
      ].map((action) => (
        <Typography component="li" key={action} sx={{ ...TIP_TEXT, py: '1px' }}>
          {action}
        </Typography>
      ))}
    </Box>
  </Box>
);

/**
 * Capability Levels: one row per capability for a team and role, lowest %
 * first and not assessed last. Opening a row shows every employee in the role
 * with their level, each opening onto the Roleplays and Quiz Questions that set
 * it, then its Breakdown (how the % is worked out).
 */
const CapabilityGapsList: React.FC<{ rows: CapabilityGapRow[] }> = ({ rows }) => {
  const [openRows, setOpenRows] = useState<string[]>([]);
  const toggleRow = (id: string) =>
    setOpenRows((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  return (
    <>
      {rows.map((row) => {
        const open = openRows.includes(row.id);
        const { percent, count, total } = row.ratio;
        const notAssessed = row.employees.length - total;
        return (
          <Box key={row.id} sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, ...fadeInSx }}>
            {/* Capability row: the capability as the title, its team, role and % on the same line. */}
            <Box
              onClick={() => toggleRow(row.id)}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid rgba(247, 148, 32, 0.45)',
                bgcolor: 'rgba(247, 148, 32, 0.06)',
                borderRadius: '4px',
                pl: '12px',
                pr: '10px',
                py: '2px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
                minHeight: 36,
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: 'rgba(247, 148, 32, 0.12)' },
                // Phone: the capability's whole name on top (wrapping, never cut), its source and
                // team | role beneath, the % and chevron on the right of both lines.
                [MOBILE_SX]: {
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                  gridTemplateAreas: '"name name pct" "chip team pct"',
                  columnGap: '8px',
                  rowGap: '4px',
                  py: '8px',
                  // Classes, not :nth-child (emotion flags it as unsafe and the QA guard fails on that warning).
                  '& > .capgap-name': { gridArea: 'name', whiteSpace: 'normal', lineHeight: 1.3 },
                  '& > :not(.capgap-name):not(.capgap-team):not(.capgap-pct)': { gridArea: 'chip' },
                  '& > .capgap-team': { gridArea: 'team', textAlign: 'left', whiteSpace: 'normal', lineHeight: 1.3 },
                  '& > .capgap-pct': { gridArea: 'pct' }
                }
              }}
            >
              <Tooltip title={row.capabilityName} placement="bottom-start" enterDelay={300}>
                <Typography
                  className="capgap-name"
                  sx={{
                    // The name takes all the room the rest leaves, so it is cut only when nothing else can give.
                    flex: '1 1 auto',
                    color: '#FDFDFD',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 14,
                    fontWeight: 400,
                    textTransform: 'uppercase',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0
                  }}
                >
                  {row.capabilityName}
                </Typography>
              </Tooltip>
              <SourceChip compact source={sourceOf(row)} />
              {/* Team | Role sits against the %, and is the first to give when the row is tight. */}
              <Typography
                className="capgap-team"
                sx={{
                  flex: '0 1000 auto',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'right',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 11,
                  color: '#B6B6B6',
                  whiteSpace: 'nowrap'
                }}
              >
                {row.teamName}
                <Box component="span" sx={{ color: DIM, mx: '5px' }}>
                  |
                </Box>
                {row.roleName}
              </Typography>
              {/* The capability's %: nobody assessed yet is a dash. */}
              <Box
                className="capgap-pct"
                sx={{
                  flex: '0 0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '8px'
                }}
              >
                <Typography
                  sx={{
                    flexShrink: 0,
                    minWidth: 52,
                    textAlign: 'center',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: percent === null ? 11 : 14,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    color: percent === null ? '#868686' : capabilityColor(percent)
                  }}
                >
                  {percent === null ? 'Not assessed' : `${percent}%`}
                </Typography>
                <IconButton
                  aria-label={open ? `Collapse ${row.capabilityName}` : `Expand ${row.capabilityName}`}
                  sx={{ flexShrink: 0, width: 22, height: 22, p: 0, color: '#FDFDFD' }}
                >
                  {open ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </Box>
            </Box>

            <Collapse in={open} unmountOnExit>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Everyone in the role, with their level. */}
                <GapSection
                  title="Learners"
                  right={<InfoTip placement="bottom-start" title={EMPLOYEES_TIP} sx={{ display: 'block' }} />}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {row.employees.map((employee) => (
                      <EmployeeRow
                        key={`${row.id}|${employee.id}`}
                        employee={employee}
                        capabilityName={row.capabilityName}
                        capabilityId={row.capabilityId}
                        criteria={row.criteria ?? {}}
                      />
                    ))}
                  </Box>
                </GapSection>

                {/* How this capability's % is worked out. */}
                <GapSection title="Breakdown">
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <BreakdownLine label="Learners in this role" value={row.employees.length} />
                    <BreakdownLine label="Not yet assessed" value={notAssessed} />
                    <BreakdownLine label="Assessed" value={total} />
                    <BreakdownLine label="Capable or better" value={count} />
                    <BreakdownLine
                      total
                      label="Capable or better"
                      value={
                        percent === null ? (
                          'Not assessed'
                        ) : (
                          <>
                            {count} of {total} assessed ={' '}
                            <Box component="span" sx={{ color: capabilityColor(percent), fontWeight: 700 }}>
                              {percent}%
                            </Box>
                          </>
                        )
                      }
                    />
                  </Box>
                </GapSection>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </>
  );
};

export default CapabilityGapsList;
